import {
  Page,
  Content,
  InfoCard,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import { Grid } from '@material-ui/core';
import { ClientDataTable } from '@WESCO-International/wdp-ui-components/components/table';
import { useNavigate, useParams } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';
import {
  domainOptions,
  getSystemOptionsForDomain,
  NameFormatter,
} from '../common/utils';
import { getAllEntities } from '../common/getAllEntities';

export const SystemDetailsPage = () => {
  const { domain, system } = useParams();
  const navigate = useNavigate();
  const catalogApi = useApi(catalogApiRef);

  const domainKey = (domain ?? '').toLowerCase();
  const systemKey = (system ?? '').toLowerCase();

  const domainMeta = domainOptions.find(
    d => (d.value ?? '').toLowerCase() === domainKey,
  );
  const systemMeta = getSystemOptionsForDomain(domainMeta?.value).find(
    s => (s.value ?? '').toLowerCase() === systemKey,
  );

  const {
    value: entities,
    loading,
    error,
  } = useAsync(async () => {
    const items = await getAllEntities(catalogApi, {
      filter: [{ kind: 'Component' }, { kind: 'API' }],
    });
    return items ?? [];
  }, [catalogApi]);

  const rows = useMemo(() => {
    if (!entities) return [] as any[];
    return entities
      .filter(e => {
        const sys = String(e?.spec?.system ?? '').toLowerCase();
        const dom = String(e?.spec?.domain ?? '').toLowerCase();
        const matchesSystem = sys === systemKey;
        const matchesDomain = domainKey ? dom === domainKey : true;
        return matchesSystem && matchesDomain;
      })
      .map(e => {
        const tagsMeta = e?.metadata?.tags ?? e?.spec?.tags ?? [];
        const tags = Array.isArray(tagsMeta)
          ? tagsMeta.join(', ')
          : String(tagsMeta ?? '');
        return {
          name: e?.metadata?.name ?? '-',
          kind: e?.kind ?? '-',
          type: e?.spec?.type ?? '-',
          owner: e?.spec?.owner ?? '-',
          lifecycle: e?.spec?.lifecycle ?? '-',
          description: e?.metadata?.description ?? '-',
          tags,
        };
      });
  }, [entities, systemKey, domainKey]);

  const columns = [
    { key: 'name', name: 'Name', sortable: true, formatter: NameFormatter },
    { key: 'owner', name: 'Owner', width: 140 },
    { key: 'type', name: 'Type', width: 140 },
    { key: 'lifecycle', name: 'Lifecycle', width: 140 },
    { key: 'description', name: 'Description' },
  ];

  if (!domainMeta || !systemMeta) {
    return (
      <Content>
        <ResponseErrorPanel error={new Error('Domain or System not found.')} />
      </Content>
    );
  }

  if (loading) return <Progress />;
  if (error) return <ResponseErrorPanel error={error} />;

  return (
    <Page themeId="home">
      <Content>
        <div className="pageHeader">
          <div className="wdp-heading s-5 semibold headingLarge">
            <div className="backTitleContainer">
              <a
                href="#"
                className="link backLink"
                aria-label="Back"
                title="Back"
                onClick={e => {
                  e.preventDefault();
                  navigate(`/domains/${encodeURIComponent(domainMeta.value)}`);
                }}
              >
                ←
              </a>
              <span className="wdp-heading s-5 semibold headingLarge">
                {systemMeta.label}
              </span>
            </div>
          </div>
        </div>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <InfoCard title={<span className="cardTitleSmall">About</span>}>
              <div className="aboutSection">
                <div className="aboutSectionBlock">
                  <div className="aboutSectionLabel">System</div>
                  <div className="aboutSectionValue">{systemMeta.label}</div>
                </div>
                <div className="aboutSectionDivider" />
                <div className="aboutSectionBlock">
                  <div className="aboutSectionLabel">Description</div>
                  <div
                    className="aboutSectionValue"
                    style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {systemMeta.description ?? 'No description provided.'}
                  </div>
                </div>
                <div
                  className="aboutSectionRow"
                  style={{ gridTemplateColumns: '1fr 1fr' }}
                >
                  <div className="aboutSectionCell">
                    <div className="aboutSectionLabel">Owner</div>
                    <div className="aboutSectionValue">
                      {systemMeta.owner ?? 'guest'}
                    </div>
                  </div>
                  <div className="aboutSectionCell">
                    <div className="aboutSectionLabel">Domain</div>
                    <div className="aboutSectionValue">{domainMeta.label}</div>
                  </div>
                </div>
              </div>
            </InfoCard>
          </Grid>
          <Grid item xs={12} md={8}>
            <InfoCard title="Relationship">
              {rows.length === 0 ? (
                <div className="emptyState">
                  No Relations found for this System.
                </div>
              ) : (
                <ClientDataTable
                  columns={columns}
                  rows={rows}
                  paginate
                  resize={true}
                  dense={false}
                  rowHeight={42}
                  headerRowHeight={42}
                  verticalBorder={false}
                />
              )}
            </InfoCard>
          </Grid>
        </Grid>
      </Content>
    </Page>
  );
};
